/**
 * In-app notification gate — the single choke point every notification
 * subscriber runs its list through before setting a count / auto-opening a
 * modal. Enforces BOTH halves of the documented rule
 * (see notificationPreferences.ts):
 *
 *   1. The user's in-app pref for this type must be ON. When a type is
 *      toggled OFF in settings, its notifications are suppressed entirely —
 *      no count, no auto-open. (Previously only the bell badge respected the
 *      toggle; the modals auto-opened regardless — this closes that gap.)
 *   2. `createdAt >= inAppEnabledSince[type]` — hides backlog accumulated
 *      before the user last flipped the type back ON.
 *
 * The pref is read from the store at call time (subscribers invoke this inside
 * their Firestore snapshot callbacks), so it reflects the current toggle.
 *
 * Usage (default reads `createdAt`):
 *   const enabledSince = useNotificationsStore.getState().inAppEnabledSince;
 *   const filtered = filterByEnabledSince('accessRequests', list, enabledSince);
 *
 * Usage with a custom timestamp field (delivery/review/etc. don't use
 * `createdAt` — pass an accessor):
 *   filterByEnabledSince('deliveryNotifications', list, enabledSince, n => n.deliveredAt);
 */

import type { NotificationType } from './notification.service';
import { useNotificationsStore } from '../store/notifications.store';
import type { EnabledSince } from '../store/notifications.store';

export function filterByEnabledSince<T>(
  type: NotificationType,
  notifications: T[],
  enabledSince: EnabledSince,
  getTimestamp: (n: T) => number | undefined = (n) => (n as any)?.createdAt,
): T[] {
  // Pref gate — if the user disabled this type in-app, show nothing.
  const prefs = useNotificationsStore.getState().inAppPrefs;
  if (prefs && prefs[type] === false) return [];

  const since = enabledSince[type] ?? 0;
  if (since <= 0) return notifications;
  return notifications.filter((n) => (getTimestamp(n) ?? 0) >= since);
}
